import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export const REFERENCES_PER_PAGE = 12;

export function useReferencePagination<T>(items: readonly T[], context: string) {
  const [position, setPosition] = useState({ context, page: 1 });
  const pageCount = Math.max(1, Math.ceil(items.length / REFERENCES_PER_PAGE));
  const page = position.context === context ? Math.min(position.page, pageCount) : 1;
  useEffect(() => {
    setPosition((previous) => previous.context === context && previous.page === page
      ? previous : { context, page });
  }, [context, page]);
  return {
    items: items.slice((page - 1) * REFERENCES_PER_PAGE, page * REFERENCES_PER_PAGE),
    page,
    pageCount,
    total: items.length,
    onPageChange: (next: number) => setPosition({ context, page: Math.max(1, Math.min(next, pageCount)) }),
  };
}

type Props = {
  page: number;
  pageCount: number;
  total: number;
  onPageChange: (page: number) => void;
  source: "Library" | "Top performers" | "My Ads";
};

export function ReferencePagination({ page, pageCount, total, onPageChange, source }: Props) {
  if (!total) return null;
  const first = (page - 1) * REFERENCES_PER_PAGE + 1;
  const last = Math.min(page * REFERENCES_PER_PAGE, total);
  return <nav aria-label={`${source} pagination`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-violet-100 bg-white p-3">
    <p role="status" className="text-xs text-slate-600">{first}–{last} of {total} · Page {page} of {pageCount}</p>
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" size="sm" aria-label={`Previous ${source.toLowerCase()} page`}
        disabled={page === 1} onClick={() => onPageChange(page - 1)}>
        <ChevronLeft className="h-4 w-4" />Previous
      </Button>
      <Button type="button" variant="outline" size="sm" aria-label={`Next ${source.toLowerCase()} page`}
        disabled={page === pageCount} onClick={() => onPageChange(page + 1)}>
        Next<ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  </nav>;
}
